// The Director's talk control: a camera iris. It opens to listen (with your voice), glows tungsten and breathes
// with the Director's voice when it speaks, turns slowly while it works, and spins up while connecting.
// Path data is written straight to the SVG from a rAF loop, so it never re-renders React per frame.
import { useEffect, useId, useRef } from "react";
import { director } from "../director";
import type { DirectorStatus } from "../store";

const N = 7;
const R = 100; // aperture radius in viewBox units (the barrel rings sit outside it)

function blades(open: number, twist: number): string[] {
  const r = R * (0.16 + 0.74 * open);
  const pts = Array.from({ length: N }, (_, i) => {
    const a = (i / N) * Math.PI * 2 + twist;
    return [Math.cos(a) * r, Math.sin(a) * r] as const;
  });
  return pts.map((p, i) => {
    const q = pts[(i + 1) % N];
    let dx = q[0] - p[0];
    let dy = q[1] - p[1];
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    // outward normal of the edge p->q (away from the centre)
    let nx = dy;
    let ny = -dx;
    if (nx * (p[0] + q[0]) + ny * (p[1] + q[1]) < 0) {
      nx = -nx;
      ny = -ny;
    }
    const L = R * 2.4;
    const a = `${p[0].toFixed(2)},${p[1].toFixed(2)}`;
    const b = `${(p[0] + dx * L).toFixed(2)},${(p[1] + dy * L).toFixed(2)}`;
    const c = `${(p[0] + dx * L + nx * L).toFixed(2)},${(p[1] + dy * L + ny * L).toFixed(2)}`;
    const d = `${(p[0] + nx * L).toFixed(2)},${(p[1] + ny * L).toFixed(2)}`;
    return `M${a}L${b}L${c}L${d}Z`;
  });
}

type Target = { open: number; spin: number; glow: number };
function target(status: DirectorStatus, lv: { input: number; output: number }, t: number): Target {
  switch (status) {
    case "connecting":
      return { open: 0.34 + 0.06 * Math.sin(t * 5), spin: 2.6, glow: 0.25 };
    case "listening":
      return { open: 0.66 + Math.min(0.26, lv.input * 0.5), spin: 0.06, glow: 0.45 + Math.min(0.5, lv.input) };
    case "thinking":
      return { open: 0.3 + 0.04 * Math.sin(t * 2.2), spin: 0.9, glow: 0.6 + 0.15 * Math.sin(t * 3) };
    case "speaking":
      return { open: 0.58 + Math.min(0.34, lv.output * 0.55), spin: 0.12, glow: 0.65 + Math.min(0.35, lv.output * 0.8) };
    case "error":
      return { open: 0.18, spin: 0, glow: 0.5 };
    default:
      // standby: the pilot light breathes slowly
      return { open: 0.4 + 0.02 * Math.sin(t * 0.9), spin: 0.03, glow: 0.8 + 0.15 * Math.sin(t * 1.3) };
  }
}

export function Iris({ status, size = 280, className = "" }: { status: DirectorStatus; size?: number | string; className?: string }) {
  const paths = useRef<(SVGPathElement | null)[]>([]);
  const glow = useRef<SVGCircleElement | null>(null);
  const statusRef = useRef(status);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  statusRef.current = status;

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let open = 0.4;
    let twist = 0.3;
    let g = 0.2;
    let last = performance.now();
    const draw = () => {
      const d = blades(open, twist);
      d.forEach((s, i) => paths.current[i]?.setAttribute("d", s));
      glow.current?.setAttribute("opacity", g.toFixed(3));
    };
    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const tg = target(statusRef.current, director.levels(), now / 1000);
      const k = 1 - Math.exp(-dt * 9);
      open += (tg.open - open) * k;
      g += (tg.glow - g) * k;
      if (!reduce) twist += tg.spin * dt;
      // the blades also turn a little as they open, like a real iris
      draw();
      raf = requestAnimationFrame(frame);
    };
    if (reduce) {
      // no continuous motion: settle on each state's resting shape
      const tick = () => {
        const tg = target(statusRef.current, { input: 0, output: 0 }, 0);
        open = tg.open;
        g = tg.glow;
        draw();
      };
      tick();
      const iv = window.setInterval(tick, 250);
      return () => window.clearInterval(iv);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  const ticks = Array.from({ length: 72 }, (_, i) => {
    const a = (i / 72) * Math.PI * 2;
    const long = i % 6 === 0;
    const r1 = 121;
    const r2 = long ? 129 : 125;
    return <line key={i} x1={Math.cos(a) * r1} y1={Math.sin(a) * r1} x2={Math.cos(a) * r2} y2={Math.sin(a) * r2} className={long ? "iris-tick iris-tick-long" : "iris-tick"} />;
  });

  return (
    <svg className={`iris iris-${status} ${className}`} viewBox="-140 -140 280 280" width={size} height={size} aria-hidden focusable="false">
      <defs>
        <clipPath id={`iris-clip-${uid}`}>
          <circle r={R} />
        </clipPath>
        <radialGradient id={`iris-light-warm-${uid}`}>
          <stop offset="0" stopColor="#FFF4DC" />
          <stop offset="0.35" stopColor="#FFC665" />
          <stop offset="1" stopColor="#FF9A1F" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`iris-light-cool-${uid}`}>
          <stop offset="0" stopColor="#F2F7FF" />
          <stop offset="0.4" stopColor="#D5D9E0" />
          <stop offset="1" stopColor="#8A8F99" stopOpacity="0" />
        </radialGradient>
        {/* standby: dark glass with a small warm pilot light at the centre */}
        <radialGradient id={`iris-light-standby-${uid}`}>
          <stop offset="0" stopColor="#FFE2B0" />
          <stop offset="0.07" stopColor="#FFB23E" stopOpacity="0.85" />
          <stop offset="0.2" stopColor="#5A4A3E" stopOpacity="0.45" />
          <stop offset="0.6" stopColor="#24262C" stopOpacity="0.35" />
          <stop offset="1" stopColor="#24262C" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`iris-blade-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--blade-a)" />
          <stop offset="1" stopColor="var(--blade-b)" />
        </linearGradient>
        <linearGradient id={`iris-glint-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0.13" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <circle r={136} className="iris-barrel" />
      <g>{ticks}</g>
      <circle r={112} className="iris-ring" />
      <circle r={R + 2} className="iris-well" />
      <circle
        ref={glow}
        r={R}
        className="iris-light"
        fill={status === "listening" || status === "connecting" ? `url(#iris-light-cool-${uid})` : status === "off" || status === "error" ? `url(#iris-light-standby-${uid})` : `url(#iris-light-warm-${uid})`}
        opacity={0.2}
      />
      <g clipPath={`url(#iris-clip-${uid})`}>
        {Array.from({ length: N }, (_, i) => (
          <path key={i} ref={(el) => void (paths.current[i] = el)} className="iris-blade" fill={`url(#iris-blade-${uid})`} />
        ))}
        {/* a lens glint across the glass */}
        <ellipse cx={-34} cy={-46} rx={44} ry={16} transform="rotate(-32 -34 -46)" fill={`url(#iris-glint-${uid})`} />
      </g>
      <circle r={R} className="iris-rim" />
    </svg>
  );
}
